/**
 * React bindings for the store. Every hook subscribes to exactly the keys it
 * reads, so a state change re-renders only the components that show it.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { Alert as ServerAlert, Boost, Capability, EntityMeta, EntityState, HeatingMsg, HomeModel, HomeSummary, ResolvedAccess } from '@home/shared';
import { store } from './store.js';
import { hasCapability } from './caps.js';

function useKey<T>(key: string, get: () => T): T {
  const sub = useCallback((fn: () => void) => store.subscribe(key, fn), [key]);
  return useSyncExternalStore(sub, get, get);
}

export function useConnection(): typeof store.status {
  return useKey('conn', () => store.status);
}

export function useReady(): boolean {
  return useKey('ready', () => store.ready);
}

/** True while showing the cached state from a previous session. */
export function useStale(): boolean {
  return useKey('stale', () => store.stale);
}

export function useUser(): typeof store.user {
  return useKey('user', () => store.user);
}

export function useHomes(): HomeSummary[] {
  return useKey('homes', () => store.homeList);
}

export function useHomeSummary(home: string): HomeSummary | undefined {
  const homes = useHomes();
  return homes.find((h) => h.id === home);
}

export function useModel(home: string): HomeModel | undefined {
  return useKey(`m|${home}`, () => store.home(home)?.model);
}

export function useAccess(home: string): ResolvedAccess | undefined {
  return useKey(`a|${home}`, () => store.home(home)?.access);
}

/** Server-evaluated alert rules (AlertsMsg), filtered server-side to what this user can see. */
export function useServerAlerts(home: string): ServerAlert[] {
  return useKey(`al|${home}`, () => store.serverAlerts.get(home) ?? EMPTY_ALERTS);
}
const EMPTY_ALERTS: ServerAlert[] = [];

/** Whether the user can change any setting in this home (shows the Configuration entry). */
export function useCanConfigure(home: string): boolean {
  const access = useAccess(home);
  return useMemo(
    () => !!access && (access.isAdmin || Object.values(access.entities).some((c) => c.includes('configure'))),
    [access],
  );
}

/** House heating mode, per-system heat info and room overrides (server-sent). */
export function useHeating(home: string): HeatingMsg | undefined {
  return useKey(`h|${home}`, () => store.heating.get(home));
}

/** Server's maintenance report version for a home (changes → refetch /maintenance). */
export function useMaintenanceVersion(home: string): string | undefined {
  return useKey(`mt|${home}`, () => store.maintenance.get(home));
}

export function useBoosts(home: string): Boost[] {
  return useKey(`b|${home}`, () => store.home(home)?.boosts ?? EMPTY);
}
const EMPTY: Boost[] = [];

export function useEntity(home: string, id: string): EntityState | undefined {
  return useKey(`e|${home}|${id}`, () => store.entity(home, id));
}

export function usePending(home: string, id: string): boolean {
  return useKey(`p|${home}|${id}`, () => store.isPending(home, id));
}

export function useMeta(home: string, id: string): EntityMeta | undefined {
  const model = useModel(home);
  return model?.entities[id];
}

/**
 * Subscribes to many entities at once and returns a number that changes
 * whenever any of them does. Pair with useMemo to derive a summary that only
 * recomputes (and re-renders) when its inputs move.
 */
export function useEntitiesVersion(home: string, ids: readonly string[]): number {
  const keyStr = ids.join(',');
  const sub = useCallback(
    (fn: () => void) => {
      const offs = ids.map((id) => store.subscribe(`e|${home}|${id}`, fn));
      return () => offs.forEach((o) => o());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [home, keyStr],
  );
  const get = useCallback(() => {
    let v = 0;
    for (const id of ids) v += store.version(`e|${home}|${id}`);
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, keyStr]);
  return useSyncExternalStore(sub, get, get);
}

/** Derives a value from several entities; recomputes only when one changes. */
export function useDerived<T>(home: string, ids: readonly string[], compute: () => T): T {
  const v = useEntitiesVersion(home, ids);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(compute, [v, home, ids.join(',')]);
}

export interface EntityAccess {
  canView: boolean;
  canControl: boolean;
  canConfigure: boolean;
  canHistory: boolean;
  /** Why control is unavailable, for the disabled-state hint. */
  reason: string | null;
}

export function useEntityAccess(home: string, id: string): EntityAccess {
  const access = useAccess(home);
  const meta = useMeta(home, id);
  return useMemo(() => entityAccess(access, meta, id), [access, meta, id]);
}

export function entityAccess(access: ResolvedAccess | undefined, meta: EntityMeta | undefined, id: string): EntityAccess {
  const caps: Capability[] | undefined = access?.entities[id];
  const canView = hasCapability(caps, 'view');
  const hasControl = hasCapability(caps, 'control');
  const inWindow = access?.inWindow !== false;
  let reason: string | null = null;
  if (meta?.readOnly) reason = meta.readOnlyReason ?? 'Read-only';
  else if (!inWindow) reason = 'Outside your access hours';
  else if (!hasControl) reason = 'View only';
  return {
    canView,
    canControl: hasControl && inWindow && !meta?.readOnly,
    canConfigure: hasCapability(caps, 'configure') && inWindow,
    canHistory: hasCapability(caps, 'history'),
    reason,
  };
}

/** Re-renders every `ms` while mounted (for countdowns and relative times). */
export function useTicker(ms: number, enabled = true): number {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setT(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms, enabled]);
  return t;
}

/** Re-renders once at epoch ms `at` (e.g. a status that flips at a wall-clock time); returns the time of the last render cue. */
export function useTickAt(at: number | null): number {
  const [t, setT] = useState(() => Date.now());
  useEffect(() => {
    if (at === null) return;
    // setTimeout caps at ~24.8 days; anything that far out re-arms on the next render anyway.
    const id = window.setTimeout(() => setT(Date.now()), Math.min(Math.max(0, at - Date.now()) + 250, 2 ** 31 - 1));
    return () => window.clearTimeout(id);
  }, [at]);
  return t;
}

export function useMediaQuery(q: string): boolean {
  const sub = useCallback(
    (fn: () => void) => {
      const m = window.matchMedia(q);
      m.addEventListener('change', fn);
      return () => m.removeEventListener('change', fn);
    },
    [q],
  );
  return useSyncExternalStore(sub, () => window.matchMedia(q).matches, () => false);
}
